import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  archiveSession,
  deleteSessionPermanently,
  restoreSession,
} from '../../services/training';

export default function SessionLifecycleActions({
  session,
  canEdit,
  isAdmin,
  onSessionUpdated,
  onSessionDeleted,
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!canEdit) return null;

  const run = async (action, fn) => {
    setError('');
    setBusy(action);
    try {
      await fn();
      if (action === 'delete') {
        onSessionDeleted?.();
        navigate('/app/entrenamientos/archivo', { replace: true });
      } else {
        onSessionUpdated?.();
      }
    } catch (err) {
      setError(err.message || 'No se pudo completar la acción');
    } finally {
      setBusy('');
      setConfirmDelete(false);
    }
  };

  const isArchived = Boolean(session.isArchived);

  return (
    <div className="max-w-5xl mx-auto px-6 pb-6">
      <div className="p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-3">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)]">
          Gestión de la sesión
        </p>

        {isArchived && (
          <p className="text-sm text-amber-400/90 font-medium">
            Esta sesión está archivada y oculta para los miembros.
          </p>
        )}

        {error && (
          <div className="p-3 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          {!isArchived ? (
            <button
              type="button"
              disabled={Boolean(busy)}
              onClick={() => run('archive', () => archiveSession(session.id))}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/40 text-amber-300 text-[10px] font-black uppercase tracking-widest hover:bg-amber-400/10 disabled:opacity-50"
            >
              {busy === 'archive' ? (
                <SafeIcon name="Loader" className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <SafeIcon name="Archive" className="w-3.5 h-3.5" />
              )}
              Archivar
            </button>
          ) : (
            <button
              type="button"
              disabled={Boolean(busy)}
              onClick={() => run('restore', () => restoreSession(session.id))}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-brand-green/40 text-brand-green text-[10px] font-black uppercase tracking-widest hover:bg-brand-green/10 disabled:opacity-50"
            >
              {busy === 'restore' ? (
                <SafeIcon name="Loader" className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <SafeIcon name="RotateCcw" className="w-3.5 h-3.5" />
              )}
              Restaurar
            </button>
          )}

          {isArchived && isAdmin && (
            confirmDelete ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-red-300">¿Eliminar permanentemente?</span>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => run('delete', () => deleteSessionPermanently(session.id))}
                  className="px-4 py-2 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                >
                  {busy === 'delete' ? 'Eliminando…' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => setConfirmDelete(false)}
                  className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                disabled={Boolean(busy)}
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-red-400/40 text-red-300 text-[10px] font-black uppercase tracking-widest hover:bg-red-500/10 disabled:opacity-50"
              >
                <SafeIcon name="Trash2" className="w-3.5 h-3.5" />
                Eliminar permanentemente
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
